import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { emitToast } from "../components/Toast";
import { roleLabel, STATUS_BADGE, STATUS_LABEL } from "./labels";
import s from "./Reviewer.module.css";

export function TesteDetail({ email }: { email: string }) {
  const { assessmentId = "" } = useParams();
  const nav = useNavigate();
  const [assessment, setAssessment] = useState<any>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [pending, setPending] = useState<any[]>([]);
  const [sel, setSel] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteName, setInviteName] = useState("");

  const load = useCallback(async () => {
    const [{ data: a }, { data: r }, { data: inv }] = await Promise.all([
      supabase.from("rec_assessments").select("*").eq("id", assessmentId).maybeSingle(),
      supabase.from("rec_v_ranking").select("*").eq("assessment_id", assessmentId),
      supabase.from("rec_invites").select("*").eq("assessment_id", assessmentId).eq("status", "sent").order("created_at", { ascending: false }),
    ]);
    setAssessment(a);
    const sorted = (r || []).sort((x, y) => {
      const ax = x.adjusted_score ?? -1, ay = y.adjusted_score ?? -1;
      return ay - ax;
    });
    setRows(sorted); setPending(inv || []); setLoading(false);
  }, [assessmentId]);

  useEffect(() => {
    load();
    const ch = supabase.channel(`rec-teste-${assessmentId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "rec_scores" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "rec_candidate_sessions" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "rec_invites" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [assessmentId, load]);

  const dimKeys = useMemo(() => {
    const first = rows.find((r) => Array.isArray(r.dimensions) && r.dimensions);
    return first ? first.dimensions.map((d: any) => ({ key: d.key, label: d.label, max: d.max })) : [];
  }, [rows]);

  const graded = rows.filter((r) => r.score_id);
  const compare = rows.filter((r) => sel.includes(r.session_id));
  const bestByDim: Record<string, number> = {};
  if (compare.length > 1) for (const d of dimKeys) bestByDim[d.key] = Math.max(...compare.map((r) => dimVal(r, d.key)));

  async function createInvite() {
    if (!inviteName.trim()) { emitToast("warning", "Informe o nome do candidato."); return; }
    const { data, error } = await supabase.from("rec_invites")
      .insert({ assessment_id: assessmentId, candidate_name: inviteName.trim(), created_by: email })
      .select().single();
    if (error) { emitToast("error", error.message); return; }
    const link = `${window.location.origin}/t/${data.token}`;
    try { await navigator.clipboard.writeText(link); emitToast("success", "Convite criado e link copiado."); }
    catch { emitToast("info", link); }
    setInviteName("");
    load();
  }

  if (loading) return <div className={s.page}><p className="muted">Carregando…</p></div>;
  if (!assessment) return <div className={s.page}><div className={s.empty}>Teste não encontrado.</div></div>;

  return (
    <div className={s.page}>
      <div className={s.crumb}>
        <a onClick={() => nav("/admin")}>Vagas</a> <span>/</span>
        <a onClick={() => nav(`/admin/vaga/${assessment.role}`)}>{roleLabel(assessment.role)}</a> <span>/</span>
        <span>{assessment.title}</span>
      </div>
      <div className={s.h}>
        <div>
          <h2>{assessment.title}</h2>
          <p className="dim">v{assessment.version} · {assessment.is_published ? "publicado" : "rascunho"} · {graded.length} corrigidos de {rows.length} candidatos</p>
        </div>
      </div>

      <div className="card" style={{ padding: "var(--s-4)", display: "flex", gap: "var(--s-2)", alignItems: "center", flexWrap: "wrap" }}>
        <strong style={{ fontSize: 14 }}>Convidar candidato:</strong>
        <input placeholder="Nome do candidato" value={inviteName} onChange={(e) => setInviteName(e.target.value)}
          style={{ background: "var(--ink-2)", border: "1px solid var(--ink-3)", borderRadius: "var(--r-md)", color: "var(--cream-1)", padding: "8px 10px", fontSize: 14, flex: "1 1 220px" }} />
        <button className="btn btn-primary" onClick={createInvite}>Gerar link da prova</button>
      </div>

      {pending.length > 0 && (
        <div>
          <h3>Convites pendentes <span className="dim" style={{ fontWeight: 400, fontSize: 13 }}>· aguardando o candidato iniciar</span></h3>
          <div className={s.scrollx}>
            <table className={s.table}>
              <thead><tr><th>Candidato</th><th>Status</th><th>Criado</th><th>Link</th></tr></thead>
              <tbody>
                {pending.map((iv) => (
                  <tr key={iv.id}>
                    <td>{iv.candidate_name || "—"}</td>
                    <td><span className="badge badge-warning">Enviado</span></td>
                    <td className="dim">{new Date(iv.created_at).toLocaleString("pt-BR")}</td>
                    <td><div className={s.copyrow}><code>{linkFor(iv.token)}</code><button className="btn btn-ghost" style={{ padding: "4px 10px" }} onClick={() => copyLink(iv.token)}>copiar</button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pending.length > 0 && <h3 style={{ marginTop: "var(--s-4)" }}>Candidatos que iniciaram</h3>}
      {rows.length === 0 ? <div className={s.empty}>Nenhum candidato iniciou a prova ainda.</div> : (
        <div className={s.scrollx}>
          <table className={s.table}>
            <thead><tr>
              <th></th><th>#</th><th>Candidato</th><th>Status</th><th className={s.num}>Base</th>
              {dimKeys.map((d: any) => <th key={d.key} className={s.num} title={d.label}>{short(d.label)}</th>)}
              <th className={s.num}>Penal.</th><th className={s.num}>Ajustado</th><th className={s.num}>Flags</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.session_id}>
                  <td>{r.score_id ? <input type="checkbox" checked={sel.includes(r.session_id)} onChange={() => setSel((p) => p.includes(r.session_id) ? p.filter((x) => x !== r.session_id) : [...p, r.session_id])} /> : null}</td>
                  <td className={s.score}>{r.score_id ? i + 1 : "—"}</td>
                  <td className={s.rowlink} onClick={() => nav(`/admin/review/${r.session_id}`)}>{r.candidate_name || "—"}</td>
                  <td><span className={`badge ${STATUS_BADGE[r.session_status] || "badge-neutral"}`}>{STATUS_LABEL[r.session_status] || r.session_status}{r.score_status === "final" ? " ✓" : ""}</span></td>
                  <td className={s.num}>{r.base_score ?? "—"}</td>
                  {dimKeys.map((d: any) => <td key={d.key} className={s.num}>{r.score_id ? dimVal(r, d.key) : "—"}</td>)}
                  <td className={s.num} style={{ color: r.penalty_total < 0 ? "var(--danger)" : undefined }}>{r.penalty_total ?? "—"}</td>
                  <td className={`${s.num} ${s.score}`}>{r.adjusted_score ?? "—"}</td>
                  <td className={s.num}>{flagCount(r) || <span className="dim">0</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {compare.length > 1 && (
        <div>
          <div className={s.h}><h3>Confronto direto ({compare.length})</h3><span className="dim">verde = melhor na dimensão</span></div>
          <div className={s.compareGrid}>
            {compare.map((r) => {
              const maxAdj = Math.max(...compare.map((x) => x.adjusted_score || 0));
              return (
                <div key={r.session_id} className={s.compareCol}>
                  <h4>{r.candidate_name || "—"}</h4>
                  <div className={s.compareRow}><span>Ajustado</span><span className={`v ${r.adjusted_score === maxAdj ? s.winner : ""}`}>{r.adjusted_score}</span></div>
                  <div className={s.compareRow}><span>Base</span><span className="v">{r.base_score}</span></div>
                  {dimKeys.map((d: any) => {
                    const v = dimVal(r, d.key);
                    return <div key={d.key} className={s.compareRow}><span>{short(d.label)}</span><span className={`v ${v === bestByDim[d.key] && v > 0 ? s.winner : ""}`}>{v}/{d.max}</span></div>;
                  })}
                  <div className={s.compareRow}><span>Flags</span><span className="v">{flagCount(r)}</span></div>
                  <button className="btn btn-ghost btn-block" style={{ marginTop: 8 }} onClick={() => nav(`/admin/review/${r.session_id}`)}>Abrir</button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function linkFor(token: string): string { return `${window.location.origin}/t/${token}`; }
async function copyLink(token: string) {
  const link = linkFor(token);
  try { await navigator.clipboard.writeText(link); emitToast("success", "Link copiado."); }
  catch { emitToast("info", link); }
}
function dimVal(r: any, key: string): number {
  const d = (r.dimensions || []).find((x: any) => x.key === key);
  return d ? Number(d.final ?? d.ai ?? 0) : 0;
}
function flagCount(r: any): number { return Array.isArray(r.anti_cheat_flags) ? r.anti_cheat_flags.length : 0; }
function short(label: string): string { return label.length > 12 ? label.slice(0, 11) + "…" : label; }
